type GlobalWithProcess = typeof globalThis & {
  process?: {
    env?: Record<string, string | undefined>;
  };
};

const DEFAULT_PUBLIC_SCHEME = (() => {
  const env = ((globalThis as GlobalWithProcess).process?.env ?? {}) as Record<string, string | undefined>;
  const raw = (env.PUBLIC_SCHEME ?? env.NEXT_PUBLIC_PUBLIC_SCHEME ?? "https").trim();
  return raw.length > 0 ? raw : "https";
})();

const DEFAULT_PUBLIC_HOST = (() => {
  const env = ((globalThis as GlobalWithProcess).process?.env ?? {}) as Record<string, string | undefined>;
  const raw = (env.PUBLIC_HOST ?? env.NEXT_PUBLIC_PUBLIC_HOST ?? "localhost").trim();
  return raw.length > 0 ? raw : "localhost";
})();

const DEFAULT_USERS_BASE_URL = `${DEFAULT_PUBLIC_SCHEME}://${DEFAULT_PUBLIC_HOST}:8083/users`;

function readEnv(): Record<string, string | undefined> {
  return ((globalThis as GlobalWithProcess).process?.env ?? {}) as Record<string, string | undefined>;
}

export type ForwardUsersRequestInit = RequestInit & {
  readonly baseUrl?: string;
};

export function getUsersServiceBaseUrl(): string {
  const raw = readEnv().USERS_URL?.trim();
  return raw && raw.length > 0 ? raw : DEFAULT_USERS_BASE_URL;
}

export function resolveUsersUrl(path: string, baseUrl: string = getUsersServiceBaseUrl()): string {
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const relativePath = path.startsWith("/") ? path.slice(1) : path;
  return new URL(relativePath, normalizedBase).toString();
}

export async function forwardUsersRequest(req: Request, path: string, init: ForwardUsersRequestInit = {}): Promise<Response> {
  const { baseUrl, ...requestInit } = init;
  const targetUrl = resolveUsersUrl(path, baseUrl ?? getUsersServiceBaseUrl());
  const method = requestInit.method ?? req.method ?? "GET";
  const headers = new Headers(requestInit.headers ?? {});

  if (!headers.has("Content-Type") && method !== "GET" && method !== "HEAD") {
    const contentType = req.headers.get("content-type");
    headers.set("Content-Type", contentType ?? "application/json");
  }

  const requestId = req.headers.get("x-request-id");
  if (requestId && !headers.has("X-Request-ID")) {
    headers.set("X-Request-ID", requestId);
  }

  const body = requestInit.body ?? (method === "GET" || method === "HEAD" ? undefined : await req.text());

  const upstreamResponse = await fetch(targetUrl, {
    ...requestInit,
    method,
    headers,
    body,
    cache: "no-store",
  });

  const responseBody = await upstreamResponse.text();
  const responseHeaders = new Headers(upstreamResponse.headers);

  const upstreamRequestId = upstreamResponse.headers.get("x-request-id");
  if (upstreamRequestId) {
    responseHeaders.set("x-request-id", upstreamRequestId);
  }

  return new Response(responseBody, {
    status: upstreamResponse.status,
    headers: responseHeaders,
  });
}
